import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  Block as VoidIcon,
  Refresh as RefreshIcon,
  MarkEmailRead as MailedIcon,
} from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import agent from '../api/axios';
import { PageHeader } from '../components/ui';
import type { Form1099Record, Correct1099Request } from '../types/api';
import { useTaxYears } from '../hooks/useApi';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

export default function Form1099Records() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<Form1099Record[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const { data: years = [] } = useTaxYears();

  // Use the first available year if current year not in list
  useEffect(() => {
    if (years.length > 0 && !years.includes(year)) {
      setYear(years[0]);
    }
  }, [years, year]);

  // Correction dialog state
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Form1099Record | null>(
    null
  );
  const [correctionForm, setCorrectionForm] = useState<
    Partial<Correct1099Request>
  >({
    correctionReason: '',
    payeeName: '',
    address1: '',
    city: '',
    state: '',
    postalCode: '',
    amount: undefined,
  });
  const [submitting, setSubmitting] = useState(false);

  // Void dialog state
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const data = await agent.Form1099.list(year);
      setRecords(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch 1099 records:', err);
      setError('Failed to load 1099 records');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleDownload = async (record: Form1099Record) => {
    try {
      const blob = await agent.Form1099.download(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `1099-${record.taxYear}-${record.payeeName}-${record.formType}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('1099 downloaded', { variant: 'success' });
    } catch (err) {
      console.error('Failed to download:', err);
      enqueueSnackbar('Failed to download 1099', { variant: 'error' });
    }
  };

  const handleMarkMailed = async (record: Form1099Record) => {
    try {
      await agent.Form1099.markMailed(record.id);
      enqueueSnackbar('Marked as mailed', { variant: 'success' });
      fetchRecords();
    } catch (err) {
      console.error('Failed to mark as mailed:', err);
      enqueueSnackbar('Failed to update', { variant: 'error' });
    }
  };

  const openCorrectionDialog = (record: Form1099Record) => {
    setSelectedRecord(record);
    setCorrectionForm({
      correctionReason: '',
      payeeName: record.payeeName,
      address1: record.address1,
      address2: record.address2 || '',
      city: record.city,
      state: record.state,
      postalCode: record.postalCode,
      amount: record.amount,
    });
    setCorrectionDialogOpen(true);
  };

  const handleSubmitCorrection = async () => {
    if (!selectedRecord) return;
    if (!correctionForm.correctionReason) {
      enqueueSnackbar('Correction reason is required', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      await agent.Form1099.correct({
        originalFormId: selectedRecord.id,
        correctionReason: correctionForm.correctionReason!,
        payeeName:
          correctionForm.payeeName !== selectedRecord.payeeName
            ? correctionForm.payeeName
            : undefined,
        address1:
          correctionForm.address1 !== selectedRecord.address1
            ? correctionForm.address1
            : undefined,
        address2:
          correctionForm.address2 !== selectedRecord.address2
            ? correctionForm.address2
            : undefined,
        city:
          correctionForm.city !== selectedRecord.city
            ? correctionForm.city
            : undefined,
        state:
          correctionForm.state !== selectedRecord.state
            ? correctionForm.state
            : undefined,
        postalCode:
          correctionForm.postalCode !== selectedRecord.postalCode
            ? correctionForm.postalCode
            : undefined,
        amount:
          correctionForm.amount !== selectedRecord.amount
            ? correctionForm.amount
            : undefined,
      });
      enqueueSnackbar('Corrected 1099 created', { variant: 'success' });
      setCorrectionDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      console.error('Failed to create correction:', err);
      enqueueSnackbar(
        err.response?.data?.message || 'Failed to create correction',
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openVoidDialog = (record: Form1099Record) => {
    setSelectedRecord(record);
    setVoidReason('');
    setVoidDialogOpen(true);
  };

  const handleVoid = async () => {
    if (!selectedRecord) return;
    if (!voidReason) {
      enqueueSnackbar('Void reason is required', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      await agent.Form1099.void({
        formId: selectedRecord.id,
        voidReason,
      });
      enqueueSnackbar('1099 voided', { variant: 'success' });
      setVoidDialogOpen(false);
      fetchRecords();
    } catch (err: any) {
      console.error('Failed to void:', err);
      enqueueSnackbar(err.response?.data?.message || 'Failed to void 1099', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getFormTypeChip = (record: Form1099Record) => {
    switch (record.formType) {
      case 'Original':
        return <Chip label="Original" color="primary" size="small" />;
      case 'Corrected':
        return (
          <Chip
            label={`Corrected #${record.correctionNumber}`}
            color="warning"
            size="small"
          />
        );
      case 'Void':
        return <Chip label="VOID" color="error" size="small" />;
      default:
        return <Chip label={record.formType} size="small" />;
    }
  };

  // Summary stats
  const totalOriginal = records.filter((r) => r.formType === 'Original').length;
  const totalCorrected = records.filter(
    (r) => r.formType === 'Corrected'
  ).length;
  const totalVoid = records.filter((r) => r.formType === 'Void').length;
  const totalAmount = records
    .filter((r) => r.formType !== 'Void')
    .reduce((sum, r) => sum + r.amount, 0);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="1099 Records & Corrections"
        subtitle="Track issued 1099 forms and manage corrections"
        icon={<DescriptionIcon />}
      />

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Original Forms
              </Typography>
              <Typography variant="h4">{totalOriginal}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Corrections
              </Typography>
              <Typography variant="h4" color="warning.main">
                {totalCorrected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Voided
              </Typography>
              <Typography variant="h4" color="error.main">
                {totalVoid}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4">
                {formatCurrency(totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tax Year</InputLabel>
              <Select
                value={year}
                label="Tax Year"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y: number) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchRecords}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Records Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : records.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No 1099 records found for {year}
            </Typography>
          </Box>
        ) : (
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Payee</TableCell>
                <TableCell>TIN</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Issued Date</TableCell>
                <TableCell>Mailed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{getFormTypeChip(record)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {record.payeeName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {record.city}, {record.state}
                    </Typography>
                    {record.correctionReason && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="warning.main"
                      >
                        Reason: {record.correctionReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{record.taxIdMasked}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(record.amount)}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(record.issuedDate), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    {record.mailedToRecipient ? (
                      <Chip
                        label={
                          record.mailedDate
                            ? format(parseISO(record.mailedDate), 'MM/dd')
                            : 'Yes'
                        }
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip label="No" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download PDF">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(record)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    {!record.mailedToRecipient &&
                      record.formType !== 'Void' && (
                        <Tooltip title="Mark as Mailed">
                          <IconButton
                            size="small"
                            onClick={() => handleMarkMailed(record)}
                          >
                            <MailedIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    {record.formType !== 'Void' && (
                      <>
                        <Tooltip title="Create Correction">
                          <IconButton
                            size="small"
                            onClick={() => openCorrectionDialog(record)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Void Form">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openVoidDialog(record)}
                          >
                            <VoidIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Correction Dialog */}
      <Dialog
        open={correctionDialogOpen}
        onClose={() => setCorrectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Corrected 1099</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            A corrected 1099 will be created with the "CORRECTED" checkbox
            marked. Only change the fields that need correction.
          </Typography>
          <TextField
            fullWidth
            required
            label="Reason for Correction"
            value={correctionForm.correctionReason}
            onChange={(e) =>
              setCorrectionForm({
                ...correctionForm,
                correctionReason: e.target.value,
              })
            }
            sx={{ mb: 2 }}
            multiline
            rows={2}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payee Name"
                value={correctionForm.payeeName}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    payeeName: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={correctionForm.address1}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    address1: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={correctionForm.city}
                onChange={(e) =>
                  setCorrectionForm({ ...correctionForm, city: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="State"
                value={correctionForm.state}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    state: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Zip"
                value={correctionForm.postalCode}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    postalCode: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={correctionForm.amount}
                onChange={(e) =>
                  setCorrectionForm({
                    ...correctionForm,
                    amount: parseFloat(e.target.value) || undefined,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSubmitCorrection}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={24} />
            ) : (
              'Create Corrected 1099'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Dialog */}
      <Dialog
        open={voidDialogOpen}
        onClose={() => setVoidDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Void 1099 Form</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This will create a void record for this 1099. The original form will
            remain in the system for audit purposes.
          </Typography>
          {selectedRecord && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Payee:</strong> {selectedRecord.payeeName}
              <br />
              <strong>Amount:</strong> {formatCurrency(selectedRecord.amount)}
            </Typography>
          )}
          <TextField
            fullWidth
            required
            label="Reason for Voiding"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleVoid}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Void 1099'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
